import {
  API_TYPES,
  type GetWorkerResponse,
  getWorkerResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { toError } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig, isDevelopmentNodeEnvironment } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  type QueryClient,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { GET_WORKER_QUERY_KEY, ONBOARDING_USER_ERROR } from "./constants";
import { type Worker } from "./types";

export const invalidateGetWorkerQuery = async (queryClient: QueryClient) => {
  await queryClient.invalidateQueries({ queryKey: [GET_WORKER_QUERY_KEY] });
};

export const GET_WORKER_PATH = "/worker";

export function useGetWorker(
  options: UseQueryOptions<Worker> = {},
  enabledRefetch = false
): UseQueryResult<Worker> {
  return useQuery({
    queryKey: [GET_WORKER_QUERY_KEY],
    queryFn: async ({ signal }) => {
      try {
        if (signal) {
          signal.addEventListener(
            "abort",
            () => {
              logEvent(APP_V2_APP_EVENTS.GET_WORKER_ABORT_SIGNAL_RECEIVED);
            },
            { once: true }
          );
        }
      } catch {
        // Ignore abort signal errors
      }

      try {
        const response = await get<undefined, GetWorkerResponse>({
          url: `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}${GET_WORKER_PATH}`,
          responseSchema: getWorkerResponse,
        });

        return {
          ...response.data.data.attributes,
          licenses: response.data.included?.filter((item) => item.type === API_TYPES.license) ?? [],
          shoppableQualifications:
            response.data.included?.filter(
              (item) => item.type === API_TYPES.workerShoppableQualification
            ) ?? [],
        };
      } catch (error) {
        const axiosError = error as AxiosError;

        /**
         * It's not ideal that we return 409 here for onboarding users, however
         * It would be a big lift to use custom ServiceError codes in worker-bff as it requires bump of util-ts
         *
         * Later in the onboarding project, we will stop calling this endpoint for onboarding users
         */
        if (axiosError.isAxiosError && axiosError.status === 409) {
          throw new Error(ONBOARDING_USER_ERROR);
        }

        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_FAILURE,
    },
    refetchOnWindowFocus: enabledRefetch,
    refetchOnMount: enabledRefetch,
    refetchOnReconnect: enabledRefetch,
    useErrorBoundary: (maybeError: unknown) => {
      if (!isDevelopmentNodeEnvironment()) {
        return false;
      }

      const error = toError(maybeError);

      // handles onboarding user error from both backend main and worker bff
      return error.message !== ONBOARDING_USER_ERROR;
    },
    ...options,
  });
}
